import React, { useState, useCallback, useRef } from 'react';
import {
    Paper,
    Typography,
    Button,
    useTheme,
    Box
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled, alpha } from '@mui/material/styles';

const StyledDropZone = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'dragging' && prop !== 'variant',
})(({ theme, dragging, variant }) => {
    const isFull = variant === 'full';
    
    return {
        width: '100%',
        minHeight: isFull ? theme.spacing(30) : theme.spacing(12),
        display: 'flex',
        flexDirection: isFull ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        padding: isFull ? theme.spacing(4) : theme.spacing(2),
        border: `2px dashed ${isFull ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        background: dragging
            ? alpha(theme.palette.primary.main, 0.15)
            : isFull
                ? theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.primary.main, 0.05)
                : theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[50],
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        boxShadow: isFull ? theme.shadows[2] : 'none',
        '&:hover': {
            background: isFull
                ? theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.primary.main, 0.1)
                : theme.palette.mode === 'dark'
                    ? theme.palette.grey[700]
                    : theme.palette.grey[100],
            borderColor: theme.palette.primary.main,
            boxShadow: isFull ? theme.shadows[4] : 'none',
            transform: isFull ? 'translateY(-2px)' : 'none',
        },
        '&.dragging': {
            background: alpha(theme.palette.primary.main, isFull ? 0.15 : 0.05),
            borderColor: isFull ? theme.palette.primary.dark : theme.palette.primary.main,
            boxShadow: isFull ? theme.shadows[6] : 'none',
        },
    };
});

const ContentWrapper = styled(Box)(({ theme, variant }) => ({
    display: 'flex',
    flexDirection: variant === 'full' ? 'column' : 'column',
    alignItems: variant === 'full' ? 'center' : 'flex-start',
    justifyContent: 'center',
    gap: variant === 'full' ? 0 : theme.spacing(0.5),
    flex: variant === 'small' ? 1 : 'none',
    textAlign: variant === 'full' ? 'center' : 'left',
    minWidth: 0, // Allow text to shrink
}));

/**
 * Reusable File Drop Zone Component
 * 
 * Supports two variants:
 * - 'full': Large, centered drop zone (for initial file selection)
 * - 'small': Compact, horizontal drop zone (for adding more files)
 * 
 * @param {Object} props
 * @param {'full'|'small'} props.variant - Drop zone variant
 * @param {Function} props.onFilesSelected - Callback when files are selected (receives FileList)
 * @param {string} props.accept - File accept attribute (e.g., image/* or all files)
 * @param {boolean} props.multiple - Allow multiple file selection
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle/description text
 * @param {string} props.buttonText - Button text
 * @param {ReactNode} props.icon - Custom icon (defaults to CloudUploadIcon)
 * @param {Object} props.sx - Additional MUI sx styles
 * @param {number} props.maxFiles - Maximum number of files allowed
 * @param {number} props.maxFileSize - Maximum file size in bytes
 * @param {boolean} props.disabled - Disable the drop zone
 */
export const FileDropZone = ({
    variant = 'full',
    onFilesSelected,
    accept = '*/*',
    multiple = false,
    title,
    subtitle,
    buttonText,
    icon,
    sx,
    maxFiles,
    maxFileSize,
    disabled = false,
    ...otherProps
}) => {
    const theme = useTheme();
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const isFull = variant === 'full';

    // Default content based on variant
    const defaultTitle = isFull 
        ? `Drop ${accept.includes('image') ? 'images' : 'files'} here or click to select`
        : `Drop more ${accept.includes('image') ? 'images' : 'files'} here or click to add`;
    
    const defaultSubtitle = accept.includes('image')
        ? 'JPG, PNG, GIF, BMP, WebP, SVG'
        : 'All file types supported';
    
    const defaultButtonText = isFull
        ? `Select ${accept.includes('image') ? 'Images' : 'Files'}`
        : 'Add Files';

    const displayTitle = title || defaultTitle;
    const displaySubtitle = subtitle || defaultSubtitle;
    const displayButtonText = buttonText || defaultButtonText;
    const displayIcon = icon || <CloudUploadIcon sx={{ fontSize: isFull ? '4rem' : '2.5rem', color: theme.palette.primary.main }} />;

    const validateFiles = useCallback((files) => {
        const fileArray = Array.from(files);
        
        // Check max files
        if (maxFiles && fileArray.length > maxFiles) {
            throw new Error(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`);
        }
        
        // Check max file size
        if (maxFileSize) {
            const oversizedFiles = fileArray.filter(file => file.size > maxFileSize);
            if (oversizedFiles.length > 0) {
                throw new Error(`File${oversizedFiles.length > 1 ? 's' : ''} exceed maximum size of ${(maxFileSize / 1024 / 1024).toFixed(2)}MB`);
            }
        }
        
        return fileArray;
    }, [maxFiles, maxFileSize]);

    const handleFiles = useCallback((files) => {
        if (!files || files.length === 0) return;
        
        try {
            const validatedFiles = validateFiles(files);
            // Pass FileList for multiple, single File for single
            if (multiple) {
                // Create a DataTransfer object to simulate FileList
                const dataTransfer = new DataTransfer();
                validatedFiles.forEach(file => dataTransfer.items.add(file));
                onFilesSelected(dataTransfer.files);
            } else {
                // For single file, pass the file directly
                onFilesSelected(validatedFiles[0]);
            }
        } catch (error) {
            console.error('File validation error:', error);
            // You can add error handling UI here if needed
        }
    }, [onFilesSelected, multiple, validateFiles]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        if (disabled) return;
        
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            handleFiles(files);
        }
    }, [handleFiles, disabled]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragging(true);
        }
    }, [disabled]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleClick = useCallback(() => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [disabled]);

    const handleFileInputChange = useCallback((e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFiles(files);
        }
        // Reset input to allow selecting same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [handleFiles]);

    return (
        <>
            <StyledDropZone
                variant={variant}
                dragging={isDragging}
                elevation={isFull ? 2 : 0}
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                    opacity: disabled ? 0.6 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    ...sx,
                }}
                className={isDragging ? 'dragging' : ''}
                {...otherProps}
            >
                {isFull ? (
                    <>
                        {displayIcon}
                        <Typography variant="h6" fontWeight={500}>
                            {displayTitle}
                        </Typography>
                        {displaySubtitle && (
                            <Typography variant="body2" color="text.secondary">
                                {displaySubtitle}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<CloudUploadIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                            }}
                            disabled={disabled}
                            sx={{ marginTop: theme.spacing(1) }}
                        >
                            {displayButtonText}
                        </Button>
                    </>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {displayIcon}
                        </Box>
                        <ContentWrapper variant={variant} theme={theme}>
                            <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.2 }}>
                                {displayTitle}
                            </Typography>
                            {displaySubtitle && (
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                    {displaySubtitle}
                                </Typography>
                            )}
                        </ContentWrapper>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                            }}
                            disabled={disabled}
                            sx={{ flexShrink: 0 }}
                        >
                            {displayButtonText}
                        </Button>
                    </>
                )}
            </StyledDropZone>
            
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
                disabled={disabled}
            />
        </>
    );
};

